import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography
} from '@mui/material';
import { DialogTitleClose } from '../../dialog';
import TestReportTable from './TestReportTable';
import { getDivisionLabel } from '../../../utils/divisionUtils';

const TestReportManagerDialog = ({
  open,
  onClose,
  gproject,
  itemsWithDocus,
  selectedProps = [], // 체크된 항목들의 ID 배열
  onUpdate, // itemsWithDocus를 업데이트하기 위한 콜백 함수
}) => {
  const [testReports, setTestReports] = useState([]);

  useEffect(() => {
    if (open) {
      const extractedReports = extractTestReportData(itemsWithDocus, selectedProps);
      console.log('성적서 데이터 추출:', extractedReports);
      console.log('itemsWithDocus 변경 감지 - 업데이트된 성적서 데이터:', extractedReports.length, '개');
      setTestReports(extractedReports);
    }
  }, [open, itemsWithDocus, selectedProps]);

  // 성적서 데이터 추출 함수
  const extractTestReportData = (itemsWithDocus, selectedProps) => {
    const reports = [];
    console.log('itemsWithDocus:', itemsWithDocus);
    console.log('selectedProps:', selectedProps);

    // 공급업체별 첨부서류 표와 동일한 구조로 데이터 추출
    itemsWithDocus
      .filter(item => ['SUPPLIER', 'RAW_MATERIAL', 'PROCESS', 'SUB_MATERIAL_PROCESS', 'SUB_MATERIAL_BUILD'].includes(item.id))
      .forEach(item => {
        // 체크된 항목은 제외
        if (selectedProps.includes(item.id)) {
          console.log('제외된 구분:', item.id, item.label);
          return;
        }

        const divisionLabel = getDivisionLabel(item.id);
        console.log('구분 처리:', item.id, divisionLabel);

        // 구분 노드의 하위에 있는 업체들을 처리
        const companies = item.children?.filter(companyNode => !selectedProps.includes(companyNode.id)) || [];
        
        companies.forEach(companyNode => {
          // 업체명 추출 (공급업체별 첨부서류 표와 동일한 방식)
          const companyName = companyNode.label ? companyNode.label.split(':')[0] : companyNode.label;
          console.log('업체 처리:', companyName);

          // gclientId 추출
          const gclientId = companyNode.data?.gclientId || companyNode.id;

          const opened = companyNode.data?.gclient?.opened;
          console.log('opened:', opened);

          // 업체 노드의 하위에서 성적서 데이터 찾기
          const testNode = companyNode.children?.find(child => child.type === 'TESTS');
          if (testNode && testNode.children) {
            console.log('TESTS 노드 발견:', testNode.label, 'children:', testNode.children.length);
            
            // 구분에 따른 제품명 목록 가져오기
            let productNames = [];
            
            // doc.data.documentPath를 사용하는 경우
            // 1. 정확한 성적서
            // 2. 대체성적서 기능으로 첨부한 성적서 (substitute: "Y")
            // 3. 가공의 경우 동일한 사양의 KCC글라스 성적서 (gclientId !== gclient.id && gclient.id === 'cfbad6d3-019e-409e-b5b8-5325e19e59b6')
            // 그 외, 가공의 경우 성능확인서는 doc.data.performanceData.performanceDocumentPath
            if (item.id === 'RAW_MATERIAL') {
              // 원판: TESTS > children > children의 code와 label 조합
              productNames = testNode.children?.flatMap(child => 
                (child.children || []).map((doc, docIndex) => {
                  // 딱 맞는 성적서가 있는지 확인 (원본 성적서만 체크, 대체성적서는 제외)
                  const hasExactTestReport = !!doc.data?.documentPath && doc.data?.substitute !== 'Y';
                  
                  // 대체성적서 확인 (substitute === 'Y'이고 documentPath가 있는 경우)
                  const hasSubstituteReport = doc.data?.substitute === 'Y' && !!doc.data?.documentPath;

                  // 분류 정보 추출
                  let classification = "";
                  let classificationFt = "";
                  const selectedClassifications = doc.data?.selectedClassifications;
                  if (selectedClassifications && Array.isArray(selectedClassifications)) {
                    const selectedClassification = selectedClassifications.filter(c => c.checked === true);
                    if (selectedClassification.length > 0) {
                      classification = selectedClassification[0].type;
                      classificationFt = selectedClassification[0].detail?.type0 || "";
                    }
                  }
                  
                  return {
                      name: `${doc.data?.code || ''} ${child.label || ''}`.trim(),
                      hasExactTestReport: hasExactTestReport, // 원본 성적서만 체크
                      hasTestReport: !!doc.data?.documentPath,
                      hasPerformanceReport: false, // 원판의 경우 성능확인서 없음
                      hasSubstituteReport: hasSubstituteReport, // 대체성적서 별도 처리
                      hasComparisonDoc: !!doc.data?.gtestComparisonDocumentPath,
                      isValid: doc.data?.substitute === 'Y' ? doc.data?.valid : doc.data?.valid,
                      isValidYN: doc.data?.substitute === 'Y' ? doc.data?.validYN : doc.data?.validYN,
                      documentPath: doc.data?.documentPath,
                      performanceDocumentPath: null, // 원판의 경우 성능확인서 없음
                      performanceData: null, // 원판의 경우 성능확인서 없음
                      substituteDocumentPath: doc.data?.substitute === 'Y' ? doc.data?.documentPath : null,
                      substituteDocumentName: doc.data?.substitute === 'Y' ? doc.data?.substituteDocumentName : null,
                      substituteDocumentType: doc.data?.substitute === 'Y' ? doc.data?.substituteDocumentType : null,
                      otherDocuments: [], // 원판의 경우 없음
                      comparisonDocumentPath: doc.data?.gtestComparisonDocumentPath,
                      g04docuGCertificationId: doc.data?.g04docuGCertificationId,
                      g04docuGCertificationName: doc.data?.g04docuGCertificationName,
                      g04docuGTestId: doc.data?.g04docuGTestId, // g04docuGTestId 추가
                      gcomponentItemName: doc.data?.gcomponentItemName, // 원판인 경우 gcomponentItemName 추가
                      gstandardNo: doc.data?.gstandardNo,
                      opened: opened,
                      gclient: doc.data?.gclient,
                      gclientId: doc.data?.gclientId,
                      id: doc.id,
                      isModifiedComparisonOnly: doc.data?.isModifiedComparisonOnly, // isModifiedComparisonOnly 추가
                      classification: classification,
                      classificationFt: classificationFt,
                      gstandardId: doc.data?.gstandardId || null,
                      folderSource: !!doc.data?.folderSource,
                      productFolderId: doc.data?.productFolderId || null,
                      code: doc.data?.code || null,
                      substituteFolderId: doc.data?.substituteFolderId || null,
                    };
                })
              ) || [];
            } else if (item.id === 'PROCESS') {
              // 가공: TESTS > children > children의 label
              productNames = testNode.children?.flatMap(child => 
                (child.children || []).map((doc, docIndex) => {
                  console.log('doc:', doc);
                  // 시험성적서와 성능확인서 확인
                  const hasTestReport = !!doc.data?.documentPath;
                  const hasPerformanceReport = !!doc.data?.performanceData?.performanceDocumentPath;
                  
                  // 딱 맞는 성적서가 있는지 확인 (원본 성적서만 체크, 대체성적서는 제외)
                  const hasExactTestReport = hasTestReport && doc.data?.substitute !== 'Y';
                  
                  // 대체성적서 확인 (substitute === 'Y'이고 documentPath가 있는 경우)
                  const hasSubstituteReport = doc.data?.substitute === 'Y' && !!doc.data?.documentPath;
                  
                  // 성능확인서 정보 추출
                  const performanceData = doc.data?.performanceData;
                  const performanceDocumentPath = performanceData?.performanceDocumentPath;
                  
                  // 분류 정보 추출
                  let classification = "";
                  let classificationFt = "";
                  const selectedClassifications = doc.data?.selectedClassifications;
                  if (selectedClassifications && Array.isArray(selectedClassifications)) {
                    const selectedClassification = selectedClassifications.filter(c => c.checked === true);
                    if (selectedClassification.length > 0) {
                      classification = selectedClassification[0].type;
                      classificationFt = selectedClassification[0].detail?.type0 || "";
                    }
                  }
                  
                  // 기타 유사한 성적서(KCC 가공유리 성적서, 유리원판대표명으로 취득한 성적서) 정보 추출 (datas에 들어있음. 추후 후보성적서 논리 보강시 보완 필요)
                  const otherDocuments = [];
                  if (doc.datas && Array.isArray(doc.datas) && doc.datas.length > 0) {
                    doc.datas.forEach(data => {
                      otherDocuments.push({
                        g04docuGTestId: data.g04docuGTestId,
                        type: data.gclientId !== data.gclient.id && data.gclient.id === 'cfbad6d3-019e-409e-b5b8-5325e19e59b6' ? 'kcc' : 'representative',
                        name: data.name,
                        companyName: data.gclientId !== data.gclient.id && data.gclient.id === 'cfbad6d3-019e-409e-b5b8-5325e19e59b6' ? data.gclient.name : '', // 대표명 성적서는 회사명 없음
                        path: data.documentPath,
                        gclient: data.gclient,
                        gclientId: data.gclientId,                        
                        isValid: data.valid,
                        isValidYN: data.validYN,
                        // 선택한 문서에 딸려있는 시험성과대비표 경로도 포함
                        gtestComparisonDocumentPath: data.gtestComparisonDocumentPath,
                      });
                    });
                  }
                  
                  const extractedData = {
                    name: doc.label || child.label || '',
                    hasExactTestReport: hasExactTestReport, // 원본 성적서만 체크
                    hasTestReport: hasTestReport,
                    hasPerformanceReport: hasPerformanceReport,
                    hasSubstituteReport: hasSubstituteReport, // 대체성적서 별도 처리
                    hasComparisonDoc: !!doc.data?.gtestComparisonDocumentPath,
                    isValid: doc.data?.substitute === 'Y' ? doc.data?.valid : doc.data?.valid,
                    isValidYN: doc.data?.substitute === 'Y' ? doc.data?.validYN : doc.data?.validYN,
                    documentPath: doc.data?.documentPath,
                    performanceDocumentPath: performanceDocumentPath,
                    performanceData: performanceData, // 전체 성능확인서 데이터
                    substituteDocumentPath: doc.data?.substitute === 'Y' ? doc.data?.documentPath : null,
                    substituteDocumentName: doc.data?.substitute === 'Y' ? doc.data?.substituteDocumentName : null,
                    substituteDocumentType: doc.data?.substitute === 'Y' ? doc.data?.substituteDocumentType : null,
                    otherDocuments: otherDocuments, // 기타 문서 목록 (대체성적서 제외)
                    comparisonDocumentPath: doc.data?.gtestComparisonDocumentPath,
                    g04docuGCertificationId: doc.data?.g04docuGCertificationId,
                    g04docuGCertificationName: doc.data?.g04docuGCertificationName,
                    g04docuGTestId: doc.data?.g04docuGTestId, // g04docuGTestId 추가
                    gstandardNo: doc.data?.gstandardNo,
                    opened: opened,
                    gclient: doc.data?.gclient,
                    gclientId: doc.data?.gclientId,
                    id: doc.id,
                    isModifiedComparisonOnly: doc.data?.isModifiedComparisonOnly, // isModifiedComparisonOnly 추가
                    classification: classification,
                      classificationFt: classificationFt,
                      gstandardId: doc.data?.gstandardId || null,
                    folderSource: !!doc.data?.folderSource,
                    productFolderId: doc.data?.productFolderId || null,
                    code: doc.data?.code || null,
                    substituteFolderId: doc.data?.substituteFolderId || null,
                  };
                  if (hasSubstituteReport) {
                    console.log('가공 대체성적서 데이터 추출:', {
                      docData: {
                        valid: doc.data?.valid,
                        validYN: doc.data?.validYN,
                        substitute: doc.data?.substitute,
                        documentPath: doc.data?.documentPath
                      },
                      extractedData: {
                        isValid: extractedData.isValid,
                        isValidYN: extractedData.isValidYN,
                        hasSubstituteReport: extractedData.hasSubstituteReport
                      }
                    });
                  }
                  return extractedData;
                })
              ) || [];
            } else if (item.id === 'SUB_MATERIAL_PROCESS' || item.id === 'SUB_MATERIAL_BUILD') {
              // 가공부자재/시공부자재: TESTS > children > children > children의 label과 상위 label 조합
              console.log(testNode);
              productNames = testNode.children?.flatMap(child => 
                child.children?.flatMap(subChild => 
                  (subChild?.children || []).map((doc, docIndex) => {
                    // 딱 맞는 성적서가 있는지 확인 (원본 성적서만 체크, 대체성적서는 제외)
                    const hasExactTestReport = !!doc.data?.documentPath && doc.data?.substitute !== 'Y';
                    
                    // 대체성적서 확인 (substitute === 'Y'이고 documentPath가 있는 경우)
                    const hasSubstituteReport = doc.data?.substitute === 'Y' && !!doc.data?.documentPath;
                    
                    // 분류 정보 추출
                    let classification = "";
                    let classificationFt = "";
                    const selectedClassifications = doc.data?.selectedClassifications;
                    if (selectedClassifications && Array.isArray(selectedClassifications)) {
                      const selectedClassification = selectedClassifications.filter(c => c.checked === true);
                      if (selectedClassification.length > 0) {
                        classification = selectedClassification[0].type;
                        classificationFt = selectedClassification[0].detail?.type0 || "";
                      }
                    }
                    
                    return {
                      name: subChild?.label === doc.label ? subChild.label : `${subChild?.label || ''} ${doc.label || ''}`.trim(),
                      hasExactTestReport: hasExactTestReport, // 원본 성적서만 체크
                      hasTestReport: !!doc.data?.documentPath,
                      hasPerformanceReport: false, // 가공부자재/시공부자재에는 성능확인서 없음
                      hasSubstituteReport: hasSubstituteReport, // 대체성적서 별도 처리
                      hasComparisonDoc: !!doc.data?.gtestComparisonDocumentPath,
                      isValid: doc.data?.valid,
                      isValidYN: doc.data?.validYN,
                      documentPath: doc.data?.documentPath,
                      performanceDocumentPath: null,
                      performanceData: null,
                      substituteDocumentPath: doc.data?.substitute === 'Y' ? doc.data?.documentPath : null,
                      substituteDocumentName: doc.data?.substitute === 'Y' ? doc.data?.substituteDocumentName : null,
                      substituteDocumentType: doc.data?.substitute === 'Y' ? doc.data?.substituteDocumentType : null,
                      otherDocuments: [], // 기타 문서 목록 (대체성적서 제외)
                      comparisonDocumentPath: doc.data?.gtestComparisonDocumentPath,
                      g04docuGCertificationId: doc.data?.g04docuGCertificationId,
                      g04docuGCertificationName: doc.data?.g04docuGCertificationName,
                      g04docuGTestId: doc.data?.g04docuGTestId, // g04docuGTestId 추가
                      gstandardNo: doc.data?.gstandardNo,
                      opened: opened,
                      gclient: doc.data?.gclient,
                      gclientId: doc.data?.gclientId,
                      id: doc.id, // TODO : 현재는 고유하지 않아 API 수정 요청해야 함
                      isModifiedComparisonOnly: doc.data?.isModifiedComparisonOnly, // isModifiedComparisonOnly 추가
                      classification: classification,
                      classificationFt: classificationFt,
                      gstandardId: doc.data?.gstandardId || null,
                      folderSource: !!doc.data?.folderSource,
                      productFolderId: doc.data?.productFolderId || null,
                      code: doc.data?.code || null,
                      substituteFolderId: doc.data?.substituteFolderId || null,
                    };
                  })
                ) || []
              ) || [];
            }

            console.log('추출된 제품명들:', productNames);
            
            // 제품명들을 성적서 데이터로 변환
            productNames.forEach(product => {
              // 대체성적서가 후보군 중에 있다면 대체성적서의 gclient를 사용
              let gclient;              
              if (product.division === 'PROCESS' && product.otherDocuments.length > 0) {
                const substituteDocument = product.otherDocuments.find(doc => doc.g04docuGTestId === product.g04docuGTestId);
                if (substituteDocument) {
                  gclient = substituteDocument.gclient;
                }
              }

              const report = {
                id: product.id,
                division: item.id, // 원본 division 값 (RAW_MATERIAL, PROCESS 등)
                divisionLabel: divisionLabel, // 한글 라벨 (원판, 가공 등)
                company: companyName,
                gclient: gclient ? gclient : product.gclient,
                gclientId: product.gclientId,
                productName: product.name,
                hasExactTestReport: product.hasExactTestReport,
                hasTestReport: product.hasTestReport,
                hasPerformanceReport: product.hasPerformanceReport,
                hasSubstituteReport: product.hasSubstituteReport,
                hasComparisonReport: product.hasComparisonDoc,
                isValid: product.isValid,
                isValidYN: product.isValidYN,
                documentPath: product.documentPath,
                performanceDocumentPath: product.performanceDocumentPath,
                performanceData: product.performanceData,
                otherDocuments: product.otherDocuments,
                substituteDocumentPath: product.substituteDocumentPath,
                substituteDocumentName: product.substituteDocumentName,
                substituteDocumentType: product.substituteDocumentType,
                comparisonDocumentPath: product.comparisonDocumentPath,
                g04docuGCertificationId: product.g04docuGCertificationId,
                g04docuGCertificationName: product.g04docuGCertificationName,
                g04docuGTestId: product.g04docuGTestId, // g04docuGTestId 추가
                gcomponentItemName: product.gcomponentItemName, // 원판인 경우 gcomponentItemName 추가
                gstandardNo: product.gstandardNo,
                opened: product.opened,
                isModifiedComparisonOnly: product.isModifiedComparisonOnly, // isModifiedComparisonOnly 추가
                classification: product.classification,
                classificationFt: product.classificationFt,
                gstandardId: product.gstandardId,
                folderSource: product.folderSource,
                productFolderId: product.productFolderId,
                code: product.code,
                substituteFolderId: product.substituteFolderId,
              };
              console.log('성적서 데이터 추가:', report);
              reports.push(report);
            });
          }
        });
      });

    console.log('최종 추출된 성적서 데이터:', reports);
    return reports;
  };

  // 제품명 생성 함수 (공급업체별 첨부서류 표의 로직 참고)
  const getProductName = (testDoc, testCategory, division) => {
    // 구분에 따른 제품명 생성 로직
    if (division === 'RAW_MATERIAL') {
      // 원판: code + label 조합
      return `${testDoc.data?.code || ''} ${testCategory.label || ''}`.trim();
    } else if (division === 'PROCESS') {
      // 가공: label만 사용
      return testDoc.label || testCategory.label || '';
    } else if (division === 'SUB_MATERIAL_PROCESS' || division === 'SUB_MATERIAL_BUILD') {
      // 가공부자재/시공부자재: 하위 스펙과 제품명 조합
      const subSpec = testCategory.label || '';
      const productName = testDoc.label || '';
      
      // 같은 이름이 반복되는 경우 처리
      if (subSpec === productName) {
        return subSpec;
      } else {
        return `${subSpec} ${productName}`.trim();
      }
    }
    
    // 기본값
    return testDoc.label || testDoc.data?.code || '';
  };

  // 인증서 정보 찾기 함수
  const findCertificationInfo = (testDoc, parentNode) => {
    // CERTIFICATIONS 타입 노드에서 해당 인증서 찾기
    const certNode = parentNode.children?.find(child => child.type === 'CERTIFICATIONS');
    if (certNode && certNode.children) {
      const certificationDoc = certNode.children.find(
        certDoc => certDoc.type === 'DOCCERTIFICATION' && 
        certDoc.data?.g04docuGCertificationId === testDoc.data?.g04docuGCertificationId
      );
      
      if (certificationDoc) {
        const standardNo = testDoc.data?.gstandardNo;
        const certName = testDoc.data?.g04docuGCertificationName;
        
        if (standardNo) {
          return {
            name: `[ ${standardNo} ]\n${certName || ''}`
          };
        } else {
          return {
            name: certName || ''
          };
        }
      }
    }
    
    // 기본값
    const standardNo = testDoc.data?.gstandardNo;
    const certName = testDoc.data?.g04docuGCertificationName;
    
    if (standardNo) {
      return {
        name: `[ ${standardNo} ]\n${certName || ''}`
      };
    } else {
      return {
        name: certName || ''
      };
    }
  };

  const handleUpdateReport = (id, newData) => {
    // 부모 컴포넌트에 업데이트 요청
    console.log(`id: ${id} 성적서 업데이트 필요`, newData);
    if (onUpdate) {
      onUpdate(id, newData);
    }
  };

  // 성적서 교체 시 itemsWithDocus 업데이트 함수
  const handleSubstituteDocumentSelected = (selectedRow, selectedDocument) => {
    console.log('=== 성적서 교체/삭제 처리 시작 ===');
    console.log('selectedRow:', selectedRow);
    console.log('selectedDocument:', selectedDocument);

    // selectedDocument가 null이면 삭제 처리
    if (!selectedDocument) {
      console.log('=== 대체성적서 삭제 처리 ===');
      
      // 트리에서 대체성적서 정보 제거
      const updatedItemsWithDocus = removeSubstituteDocumentFromTree(itemsWithDocus, selectedRow.id);
      console.log(updatedItemsWithDocus)
      // 부모 컴포넌트에 삭제 정보 전달
      if (onUpdate) {
        onUpdate(selectedRow.id, {
          type: 'substitute_document_removed',
          selectedDocument: null,
          updatedItemsWithDocus: updatedItemsWithDocus
        });
      }
      return;
    }

    console.log('=== 성적서 교체 선택 처리 ===');

    // 선택된 문서 정보만 업데이트
    const updatedItemsWithDocus = updateDocumentInTree(itemsWithDocus, selectedRow.id, selectedDocument);
    
    console.log('=== 성적서 교체 선택 완료 ===');
    
    // 부모 컴포넌트에 업데이트된 데이터 전달
    if (onUpdate) {
      onUpdate(selectedRow.id, {
        type: 'substitute_document_selected',
        selectedDocument: selectedDocument,
        updatedItemsWithDocus: updatedItemsWithDocus
      });
    }
  };

  // 트리 구조에서 대체성적서 정보를 제거하는 함수
  const removeSubstituteDocumentFromTree = (items, targetId) => {
    console.log('=== removeSubstituteDocumentFromTree 호출 ===');
    console.log('targetId:', targetId);
    console.log('입력 items:', JSON.stringify(items, null, 2));
    
    return items.map(item => {
      // 구분 노드 처리
      if (['SUPPLIER', 'RAW_MATERIAL', 'PROCESS', 'SUB_MATERIAL_PROCESS', 'SUB_MATERIAL_BUILD'].includes(item.id)) {
        const updatedChildren = item.children?.map(companyNode => {
          // 업체 노드 처리
          const updatedCompanyChildren = companyNode.children?.map(child => {
            if (child.type === 'TESTS') {
              // TESTS 노드 처리
              const updatedTestChildren = child.children?.map(testCategory => {
                // 테스트 카테고리 노드 처리. 부자재 규격은 한 단계 더 있다.
                const visitDoc = (doc) => {
                  // 문서 노드에서 targetId와 일치하는 문서 찾기
                  if (doc.id === targetId) {
                    console.log('=== 대상 문서 발견 (삭제 처리) ===');
                    console.log('doc.id:', doc.id);
                    console.log('기존 doc.data:', JSON.stringify(doc.data, null, 2));
                    
                    // 대체성적서 정보 제거
                    const updatedData = {
                      ...doc.data,
                      documentPath: null, // 대체성적서 경로 제거
                      substitute: 'N', // 대체성적서 아님으로 변경
                      substituteDocumentPath: null, // 대체성적서 경로 제거
                      substituteDocumentName: null, // 대체성적서 이름 제거
                      substituteDocumentType: null, // 대체성적서 타입 제거
                      substituteFolderId: null,
                      substituteDocumentValid: null, // 대체성적서 유효성 제거
                      substituteDocumentValidYN: null, // 대체성적서 유효성 YN 제거
                      gtestComparisonDocumentPath: null, // 시험성과대비표 경로 제거
                      // gclientId와 gclient는 원본 정보이므로 유지
                    };
                    
                    console.log('삭제 후 업데이트된 doc.data:', updatedData);
                    
                    return {
                      ...doc,
                      data: updatedData
                    };
                  }
                  if (doc.children?.length) {
                    return { ...doc, children: doc.children.map(visitDoc) };
                  }
                  return doc;
                };

                return {
                  ...testCategory,
                  children: testCategory.children?.map(visitDoc)
                };
              });

              return {
                ...child,
                children: updatedTestChildren
              };
            }
            return child;
          });

          return {
            ...companyNode,
            children: updatedCompanyChildren
          };
        });

        return {
          ...item,
          children: updatedChildren
        };
      }
      return item;
    });
  };

  // 트리 구조에서 특정 문서를 업데이트하는 함수
  const updateDocumentInTree = (items, targetId, selectedDocument) => {
    console.log('=== updateDocumentInTree 호출 ===');
    console.log('targetId:', targetId);
    console.log('selectedDocument:', selectedDocument);
    console.log('입력 items:', JSON.stringify(items, null, 2));
    
    return items.map(item => {
      // 구분 노드 처리
      if (['SUPPLIER', 'RAW_MATERIAL', 'PROCESS', 'SUB_MATERIAL_PROCESS', 'SUB_MATERIAL_BUILD'].includes(item.id)) {
        const updatedChildren = item.children?.map(companyNode => {
          // 업체 노드 처리
          const updatedCompanyChildren = companyNode.children?.map(child => {
            if (child.type === 'TESTS') {
              // TESTS 노드 처리
              const updatedTestChildren = child.children?.map(testCategory => {
                // 테스트 카테고리 노드 처리. 부자재 규격은 한 단계 더 있다.
                const visitDoc = (doc) => {
                  // 문서 노드에서 targetId와 일치하는 문서 찾기
                  if (doc.id === targetId) {
                    console.log('=== 대상 문서 발견 ===');
                    console.log('doc.id:', doc.id);
                    console.log('기존 doc.data:', JSON.stringify(doc.data, null, 2));
                    console.log('selectedDocument:', JSON.stringify(selectedDocument, null, 2));
                    
                    // 선택된 문서 정보로 업데이트
                    const updatedData = {
                      ...doc.data,
                      name: selectedDocument.name,
                    };
                    
                    // 성능확인서인지 확인 (documentType이 'performance'이거나 documentPath가 performanceDocumentPath와 일치하는 경우)
                    const isPerformanceDocument = selectedDocument.documentType === 'performance' || 
                        (selectedDocument.documentPath && doc.data?.performanceData?.performanceDocumentPath === selectedDocument.documentPath);
                    
                    console.log('성능확인서 판단:', {
                      documentType: selectedDocument.documentType,
                      documentPath: selectedDocument.documentPath,
                      existingPerformancePath: doc.data?.performanceData?.performanceDocumentPath,
                      isPerformanceDocument: isPerformanceDocument
                    });
                    
                    if (isPerformanceDocument) {
                      console.log('성능확인서 업데이트:', selectedDocument);
                      // 성능확인서는 대체성적서로 표시하지 않고 performanceData에 저장
                      updatedData.performanceData = {
                        ...doc.data?.performanceData,
                        performanceDocumentPath: selectedDocument.documentPath,
                      };
                      updatedData.substitute = 'N'; // 성능확인서는 대체성적서가 아님
                      updatedData.substituteFolderId = null;
                      // 성능확인서 선택 시 기존 대체성적서 정보만 초기화 (documentPath는 유지)
                      if (doc.data?.substitute === 'Y') {
                        updatedData.documentPath = null;
                      }
                      updatedData.gtestComparisonDocumentPath = selectedDocument.gtestComparisonDocumentPath;
                      console.log('성능확인서 업데이트:', updatedData.performanceData);
                    } else {
                      // 일반 성적서는 대체성적서로 표시
                      updatedData.documentPath = selectedDocument.documentPath;
                      updatedData.substitute = 'Y';
                      
                      // 유효성 정보 업데이트 (대체성적서일 때만)
                      if (selectedDocument.isValid !== undefined) {
                        updatedData.valid = selectedDocument.isValid;
                      }
                      if (selectedDocument.isValidYN !== undefined) {
                        updatedData.validYN = selectedDocument.isValidYN;
                      }
                      
                      // 대체성적서 이름과 타입 업데이트
                      if (selectedDocument.name !== undefined) {
                        updatedData.substituteDocumentName = selectedDocument.name;
                      }
                      if (selectedDocument.documentType !== undefined) {
                        updatedData.substituteDocumentType = selectedDocument.documentType;
                      }
                      
                      // gclientId와 gclient 정보 업데이트 (대체성적서의 원본 업체 정보)
                      if (selectedDocument.gclientId !== undefined) {
                        updatedData.gclientId = selectedDocument.gclientId;
                      }
                      if (selectedDocument.gclient !== undefined) {
                        updatedData.gclient = selectedDocument.gclient;
                      }
                      
                      // 시험성과대비표 경로 업데이트 (일반 성적서 선택 시에만)
                      if (selectedDocument.gtestComparisonDocumentPath !== undefined) {
                        updatedData.gtestComparisonDocumentPath = selectedDocument.gtestComparisonDocumentPath;
                      }

                      if (selectedDocument.g04docuGTestId !== undefined) {
                        updatedData.g04docuGTestId = selectedDocument.g04docuGTestId;
                      }
                      updatedData.substituteFolderId = selectedDocument.substituteFolderId || null;
                      
                      console.log('일반 성적서 업데이트 (gclient 정보 업데이트):', {
                        selectedDocument: selectedDocument,
                        updatedGclientId: updatedData.gclientId,
                        updatedGclient: updatedData.gclient
                      });
                    }
                    
                    console.log('업데이트된 doc.data:', updatedData);
                    
                    return {
                      ...doc,
                      data: updatedData
                    };
                  }
                  if (doc.children?.length) {
                    return { ...doc, children: doc.children.map(visitDoc) };
                  }
                  return doc;
                };

                return {
                  ...testCategory,
                  children: testCategory.children?.map(visitDoc)
                };
              });

              return {
                ...child,
                children: updatedTestChildren
              };
            }
            return child;
          });

          return {
            ...companyNode,
            children: updatedCompanyChildren
          };
        });

        return {
          ...item,
          children: updatedChildren
        };
      }
      return item;
    });
  };

  const handleAddSubstitute = (id) => {
    // 대체 성적서 추가 로직
    console.log(`id: ${id} 대체 성적서 추가`);
    // TODO: 대체 성적서 추가 기능 구현
  };

  // 시험성과대비표가 저장(수정되어 추가된 것)되거나 수정본이 재차 수정하거나 또는 수정 시험성과대비표를 삭제할 때 itemsWithDocus 업데이트 함수
  const handleComparisonDocumentUpdated = (itemsWithDocusId, documentPath, isModifiedComparisonOnly = false) => {
    console.log('=== handleComparisonDocumentUpdated 호출 ===');
    console.log({ itemsWithDocusId, documentPath, isModifiedComparisonOnly});
    
    // 트리 구조에서 시험성과대비표 경로 업데이트
    const updatedItemsWithDocus = updateComparisonDocumentInTree(itemsWithDocus, itemsWithDocusId, documentPath, isModifiedComparisonOnly);
    console.log('업데이트된 itemsWithDocus:', updatedItemsWithDocus);
    console.log('=== 시험성과대비표 업데이트 완료 ===');
    
    // 부모 컴포넌트에 업데이트된 데이터 전달
    if (onUpdate) {
      onUpdate(itemsWithDocusId, {
        type: 'comparison_document_updated',
        documentPath: documentPath,
        isModifiedComparisonOnly: isModifiedComparisonOnly,
        updatedItemsWithDocus: updatedItemsWithDocus
      });
    }
  };

  // 트리 구조에서 시험성과대비표 경로를 업데이트하는 함수
  const updateComparisonDocumentInTree = (items, targetId, documentPath, isModifiedComparisonOnly = false) => {
    console.log('=== updateComparisonDocumentInTree 호출 ===');
    console.log('targetId:', targetId);
    console.log('documentPath:', documentPath);
    console.log('isModifiedComparisonOnly:', isModifiedComparisonOnly);
    console.log('입력 items:', JSON.stringify(items, null, 2));
    
    return items.map(item => {
      // 구분 노드 처리
      if (['SUPPLIER', 'RAW_MATERIAL', 'PROCESS', 'SUB_MATERIAL_PROCESS', 'SUB_MATERIAL_BUILD'].includes(item.id)) {
        const updatedChildren = item.children?.map(companyNode => {
          // 업체 노드 처리
          const updatedCompanyChildren = companyNode.children?.map(child => {
            if (child.type === 'TESTS') {
              // TESTS 노드 처리
              const updatedTestChildren = child.children?.map(testCategory => {
                // 테스트 카테고리 노드 처리
                const updatedTestDocs = testCategory.children?.map(doc => {
                  // 문서 노드에서 targetId와 일치하는 문서 찾기
                  if (doc.id === targetId) {
                    console.log('=== 대상 문서 발견 (시험성과대비표 업데이트) ===');
                    console.log('doc.id:', doc.id);
                    console.log('기존 doc.data:', JSON.stringify(doc.data, null, 2));
                    
                    // 시험성과대비표 경로 업데이트
                    const updatedData = {
                      ...doc.data,
                      gtestComparisonDocumentPath: documentPath,
                      isModifiedComparisonOnly: isModifiedComparisonOnly
                    };
                    
                    console.log('시험성과대비표 업데이트 후 doc.data:', updatedData);
                    
                    return {
                      ...doc,
                      data: updatedData
                    };
                  }
                  return doc;
                });

                return {
                  ...testCategory,
                  children: updatedTestDocs
                };
              });

              return {
                ...child,
                children: updatedTestChildren
              };
            }
            return child;
          });

          return {
            ...companyNode,
            children: updatedCompanyChildren
          };
        });

        return {
          ...item,
          children: updatedChildren
        };
      }
      return item;
    });
  };

  const handleViewDocument = (documentPath) => {
    // 문서 보기 로직
    console.log('문서 보기:', documentPath);
    // TODO: 문서 보기 기능 구현
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      sx={{
        '& .MuiDialog-paper': {
          height: '94vh',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitleClose id="test-report-manager-dialog-title" onClose={onClose}>
        성적서 / 시험성과대비표 검토
      </DialogTitleClose>
      <DialogContent 
        sx={{ 
          p: 3, 
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* 성적서 목록을 보여줄 테이블 */}
        <TestReportTable
          gproject={gproject}
          reports={testReports}
          onAddSubstitute={handleAddSubstitute}
          onComparisonDocumentUpdated={handleComparisonDocumentUpdated}
          onViewDocument={handleViewDocument}
          onSubstituteDocumentSelected={handleSubstituteDocumentSelected}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestReportManagerDialog; 